/**
 * The seat — rolling 30-day balance (Update Directive 18 Oct 2026 §4–5).
 *
 * Acquisition (deity-progress.ts) = what you've proven you can do.
 * The seat = what you've actually been doing lately. Separate numbers.
 *
 * Every due moment produces exactly one mark — there is no neutral:
 *   deity mark   the deity's daily requirement was met (its action done right)
 *   malady mark  the requirement was skipped, or done the malady's way
 * Situational reps (a SWAP Situation fired: the Used [deity] / [Malady] won
 * buttons, dmt_events source 'manual', grade ±1) are extra marks on top.
 *
 *   Seat = 4 × (D − M) ÷ marks due, over the last `seatWindowDays` days
 *
 * Seats start at 0: intake severity never sets the seat. The score shows "—"
 * until there are `seatMinDays` days of marks. Sign = who holds the seat (flip
 * at 0, no buffer); magnitude = how firmly. Pure — no DB/React/clock.
 */
import { DEFAULT_GAME_SETTINGS, type GameSettings } from './gameSettings';
import { dayKeyOf, parseDay, requirementMet, startDayOf, type DeityProgress } from './deity-progress';

export interface SeatMark {
  /** Stable id — the same event never counts twice. */
  id: string;
  /** 'YYYY-MM-DD' local. */
  date: string;
  kind: 'deity' | 'malady';
  source: 'daily' | 'situational';
}

/** A logged situational event (dmt_events row, source 'manual'). */
export interface SituationalEvent {
  id: string;
  occurredAt: string; // ISO
  grade: number; // +1 deity used, −1 malady won
}

export type SeatHolder = 'deity' | 'malady' | 'contested';

export interface SeatScore {
  /** −4..+4, or null ("—") until `seatMinDays` days of marks. */
  score: number | null;
  /** Who holds the seat; null while the score is "—". */
  holder: SeatHolder | null;
  /** 0..1 firmness (|score| / 4); 0 while "—". */
  firmness: number;
  deityMarks: number;
  maladyMarks: number;
  due: number;
  /** Distinct days in the window that carry marks. */
  daysOfMarks: number;
}

const MS_DAY = 24 * 3600 * 1000;

/**
 * Daily marks for one deity: one mark per day from its start through
 * yesterday (a day isn't judged until it closes), plus today once met.
 */
export function dailyMarks(progress: DeityProgress | undefined, todayStr: string, game: GameSettings = DEFAULT_GAME_SETTINGS, toolId = ''): SeatMark[] {
  const start = startDayOf(progress);
  const today = parseDay(todayStr);
  if (!start || !today) return [];
  const from = Math.max(parseDay(start)!.getTime(), today.getTime() - (game.seatWindowDays - 1) * MS_DAY);
  const out: SeatMark[] = [];
  for (let t = from; t <= today.getTime() + MS_DAY / 2; t += MS_DAY) {
    const day = dayKeyOf(new Date(t));
    const met = requirementMet(progress, day, game);
    if (day === todayStr && !met) continue; // today is still open
    out.push({ id: `daily:${toolId}:${day}`, date: day, kind: met ? 'deity' : 'malady', source: 'daily' });
  }
  return out;
}

export function situationalMarks(events: SituationalEvent[]): SeatMark[] {
  return events
    .filter((e) => e.grade !== 0 && !Number.isNaN(Date.parse(e.occurredAt)))
    .map((e) => ({ id: `sit:${e.id}`, date: dayKeyOf(new Date(e.occurredAt)), kind: e.grade > 0 ? 'deity' : 'malady', source: 'situational' }));
}

/** §5 formula over the window ending `todayStr`. Duplicate ids are counted once. */
export function seatScore(marks: SeatMark[], todayStr: string, game: GameSettings = DEFAULT_GAME_SETTINGS): SeatScore {
  const today = parseDay(todayStr);
  const empty: SeatScore = { score: null, holder: null, firmness: 0, deityMarks: 0, maladyMarks: 0, due: 0, daysOfMarks: 0 };
  if (!today) return empty;
  const fromT = today.getTime() - (game.seatWindowDays - 1) * MS_DAY - MS_DAY / 2;
  const seen = new Set<string>();
  const days = new Set<string>();
  let D = 0;
  let M = 0;
  for (const m of marks) {
    if (seen.has(m.id)) continue;
    const d = parseDay(m.date);
    if (!d || d.getTime() < fromT || d.getTime() > today.getTime() + MS_DAY / 2) continue;
    seen.add(m.id);
    days.add(m.date);
    if (m.kind === 'deity') D += 1;
    else M += 1;
  }
  const due = D + M;
  if (days.size < game.seatMinDays || due === 0) return { ...empty, deityMarks: D, maladyMarks: M, due, daysOfMarks: days.size };
  const score = Math.max(-4, Math.min(4, (4 * (D - M)) / due));
  const holder: SeatHolder = score > 0 ? 'deity' : score < 0 ? 'malady' : 'contested';
  return { score, holder, firmness: Math.abs(score) / 4, deityMarks: D, maladyMarks: M, due, daysOfMarks: days.size };
}

/** Convenience: the whole seat for one deity. */
export function seatFor(
  progress: DeityProgress | undefined,
  events: SituationalEvent[],
  todayStr: string,
  game: GameSettings = DEFAULT_GAME_SETTINGS,
  toolId = '',
): SeatScore {
  return seatScore([...dailyMarks(progress, todayStr, game, toolId), ...situationalMarks(events)], todayStr, game);
}

/** Display: "+2.4", "−1.0", "—". */
export function formatSeat(s: SeatScore): string {
  if (s.score == null) return '—';
  const v = Math.abs(s.score).toFixed(1);
  return s.score > 0 ? `+${v}` : s.score < 0 ? `−${v}` : '0.0';
}
